import React from 'react';
import { useTranslation } from '../../providers/TranslationProvider';
import { Tooltip } from '../overlays/Tooltip';

/**
 * PD-14023 — shared copy for shop controls disabled because an item is
 * out of stock (the tenant's out-of-stock order-submission check is on
 * and the item has no available quantity). Passed as `message` so the
 * same wrapper explains both gate types consistently. Kept terse per
 * owner direction — the user knows what it means.
 */
export const OUT_OF_STOCK_MESSAGE = 'Out of Stock';

interface NoPermissionTooltipProps {
	/**
	 * When true, the children are wrapped in a tooltip explaining the
	 * permission gate. When false, the wrapper is a no-op and children
	 * render unchanged.
	 */
	disabled: boolean;
	/**
	 * Override the default tooltip copy. Use this for context-specific
	 * messages (e.g., "You don't have permission to place orders").
	 */
	message?: string;
	/**
	 * Position passed through to the underlying Tooltip.
	 */
	position?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
	/**
	 * Class names applied to the trigger wrapper div the underlying
	 * Tooltip renders. The wrapper participates in layout, so callers
	 * must pass whatever class the original control had as a layout
	 * participant — e.g., `flex-1` inside a flex container, or `w-full`
	 * for inputs/dropdowns that should stretch.
	 *
	 * Defaults to no class — explicit layout intent prevents the wrapper
	 * from quietly shrinking or stretching wrapped controls.
	 */
	className?: string;
	children: React.ReactNode;
}

/**
 * Wraps a control in a permission-explanation tooltip — only renders the
 * tooltip when `disabled` is true. Use to consistently explain why a
 * control is gated on the cart and shop pages.
 *
 * The tooltip-trigger div participates in layout, so callers must pass
 * `className` matching the layout context (e.g., `flex-1` inside a flex
 * container) — otherwise the wrapper collapses to its content width and
 * shrinks the wrapped control.
 */
export function NoPermissionTooltip({
	disabled,
	message,
	position = 'top',
	className,
	children,
}: NoPermissionTooltipProps): React.ReactElement {
	const { t } = useTranslation();

	if (!disabled) {
		return <>{children}</>;
	}

	const content = t(message ?? "You don't have permission to modify orders.");

	return (
		<Tooltip content={content} position={position} className={className}>
			{children}
		</Tooltip>
	);
}
